import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, AudioLines } from "lucide-react";
import { Link } from "./Navigation";
import { HeroWorkspace } from "./HeroWorkspace";
import "./hero.css";

// The original job / weekend / lunch / holiday / 2 AM scenarios, phrased for the restored headline.
const scenarios = [
  "on a job.",
  "off for the weekend.",
  "out for lunch.",
  "on holiday.",
  "asleep at 2 AM.",
];
const clamp = (value: number) => Math.max(0, Math.min(1, value));
const smooth = (value: number) => {
  const p = clamp(value);
  return p * p * (3 - 2 * p);
};
// The clip's map across the stage's whole visible life. It is not linear, and
// the shape is a pacing decision. It is THE pacing decision: the owner's note
// on the live hero was that the composition is right and the camera crawls.
//
// A pinned stage changes the frame in two very different ways. While it is
// pinned only what is inside it moves; once it releases, the whole frame
// travels with the page, which is a far larger change per pixel of scroll. So
// the pin gets the footage. The map runs the playhead fast while the stage is
// held and slower while it slides away, and hands over between the two on a
// smoothstep, so the camera eases rather than steps.
//
// It is written against the pin's own share of the life, `knot`, because that
// share is a measurement — the track height less the stage height, over the
// track height — and not a constant: a phone, a short window and an open
// consent banner all move it. The rate is
//
//   f'(p) = 1 + BOOST x ((1 - c) - S(p))    S: 0 before the handover, 1 after
//
// so it is 1 + BOOST(1 - c) through the pin and 1 - BOOST x c after it, with
// the handover opening exactly as the stage lets go rather than straddling
// that moment. That placement is deliberate: the card's rise is a smoothstep
// and its own contribution to the frame is easing to nothing over the last of
// the pin, so the clip has to still be at full rate there or the hero has a
// quiet hole immediately before its loudest stretch. (Measured at 390 before
// this change, that hole was real: the last step of the pin changed the frame
// by 9.4 and the first step of the release by 29.6.)
//
// f(0) = 0 and f(1) = 1, because S integrates to exactly (1 - c) over the
// life, so the first and last frames still land on the ends of the track; and
// f' is floored at a quarter of the average, so the playhead never stalls and
// never reverses however the pin's share comes out.
//
// The curve this replaces was symmetric: quickest at both ends and slowest in
// the middle, which spent its speed on the entry slide and the exit, the two
// places the reader is not looking at the valley.
const BOOST = 1.2;
// Half the width of the handover, as a fraction of the life. The rate glides
// from the pinned one to the released one over a band that opens at the moment
// the stage lets go — which is the moment the whole frame starts travelling
// with the page, so the deceleration is covered by a much larger change.
const HANDOVER = 0.12;
const clipMap = (value: number, pin: number) => {
  const p = clamp(value);
  const knot = Math.max(0.2, Math.min(0.6, pin));
  const w = Math.min(HANDOVER, (1 - knot) / 2);
  const c = knot + w;
  // A released stage is still on screen for a whole viewport of scroll, so its
  // share of the clip is never allowed under a quarter of the average rate,
  // whatever geometry the page turns out to have.
  const boost = Math.min(BOOST, 0.75 / c);
  // The integral of that smoothstep, in closed form, so the map stays one
  // expression evaluated per scroll event and does no numeric work.
  let s: number;
  if (p <= c - w) s = 0;
  else if (p >= c + w) s = p - c;
  else {
    const t = (p - c + w) / (2 * w);
    s = 2 * w * (t * t * t - 0.5 * t * t * t * t);
  }
  return p + boost * ((1 - c) * p - s);
};
// The near plane's own curve. A plane travelling toward the camera accelerates
// off the bottom of the frame; a smoothstep does the opposite and moves fastest
// in the middle of its range, which is why the rocks used to be off the card by
// progress 0.26 — one flick of the wheel and the occlusion the brief asks for
// was over. Quadratic ease-in holds the ground on the card through the first
// third and then drops it out. Matched by cubic-bezier(0.11, 0, 0.5, 0) on
// ch-camera-near in hero.css, which is the same curve.
const sink = (value: number) => {
  const t = clamp(value);
  return t * t;
};

// The hero's world is Dom's Mars clip: a five-second forward dolly up a
// valley, rock ridges passing on both sides and a layered mountain rising into
// the gap. Scroll scrubs it.
//
// The scrub is implemented here rather than handed to the vendored engine
// (public/scrollcraft/scrollcraft.js, never edited). The engine's scrub act
// owns the section it is put on: it writes the track height inline in vh, and
// it stamps .sc-stage on the stage, which brings its own sticky/height/z-index
// from scrollcraft.css. This hero is a bespoke sticky section that already
// declares all of that in svh, alongside its own rAF driver for the card and
// the copy, so the two were overwriting each other and the CSS only won with
// !important. The engine still runs the rest of the page; the hero carries no
// data-sc-* attributes at all.
//
// What is implemented below is the engine's contract exactly, because every
// clause of it fixes a real failure:
//   - the clip is fetched with fetch() into a Blob URL, so seeking never
//     depends on HTTP range support, and only once the hero is near;
//   - the poster holds until a real frame has painted, because iOS keeps a
//     seeked-but-never-played muted video blank and hiding the poster on
//     metadata alone flashes an empty stage;
//   - clip time is mapped across the stage's whole visible life, not its
//     pinned travel, so it does not freeze on frame one through the entry
//     slide and on its last frame through the exit;
//   - scroll only ever writes a target, and a standalone rAF loop walks the
//     playhead toward it at 0.18 a frame behind an 8 ms desktop / 20 ms phone
//     deadband, never queueing a seek while the last one is unresolved;
//   - an offscreen clip, a hidden tab and an arrived playhead are all left
//     alone;
//   - under reduced motion no video is rendered and nothing is fetched.
const CLIP = {
  src: "/mars-hero.mp4", // 1440x810, 120 frames, 5.0 s, a keyframe every 8
  srcMobile: "/mars-hero-m.mp4", // 406x720 portrait crop, a keyframe every 4
  poster: "/mars-poster.webp", // frame one, the LCP image (preloaded in index.html)
  posterMobile: "/mars-poster-m.webp",
} as const;
// The near plane. The clip is the far world; this is the ground the reader is
// standing on, and it is what covers the bottom of the product card at rest
// and travels down out of frame as they scroll, exactly as the retired grass
// plane did. A real alpha WebP, never an SVG: an SVG that references an
// external image renders blank inside an <img>, which is what broke the live
// grass on 09-06.
//
// The file is the approved rocky cutout graded to this clip: its own light was
// a warmer, more saturated dusk, the clip's is dustier ochre, so the master was
// rotated +4.52 deg toward the clip's hue, lifted with a 0.662 gamma on L and
// given 1.146x chroma with a highlight roll-off, which lands its mid-tones on
// the clip's own ground (L 52.7 against 53 to 56, hue 59.1 against 59.4 to
// 60.1, HSV saturation 0.555 against 0.527 to 0.540). Graded under a new name
// because a phone holding the old bytes would never fetch a changed file.
const NEAR = {
  src: "/rock-near-mars.webp",
  srcMobile: "/rock-near-mars-mobile.webp",
  width: 2172,
  height: 1207,
  widthMobile: 1086,
  heightMobile: 603,
} as const;
// The playhead walks a fixed fraction of the remaining distance each frame:
// wheel events do not arrive at a constant rate, so a 1:1 write reproduces
// every gap in them as a stutter.
const LERP = 0.18;

// The hero's pacing, as fractions of the pinned travel. Mirrored exactly by
// the three `animation-range` declarations in hero.css, because either driver
// may be the one running.
//
// CARD_END is 1 and not 0.82 on purpose: "end the pin when the motion ends".
// The card's rise is the hero's spine, so it now runs the whole pin instead of
// stopping 211px short of it and leaving the reader to push a finished stage
// off the screen.
const CARD_END = 1;
// The ground goes first, on the accelerating curve above. The brief's
// acceptance is the card fully clear of the plane by hero progress 0.6, and
// with `sink` the card comes clear at roughly two thirds of this range.
const NEAR_END = 0.78;
// The headline used to be at 7% opacity by y=400 and gone by y=600, the whole
// message spent in the first 29% of the hero. It now holds past halfway.
const HEAD_END = 0.5;
// The card starts at 0.8 rather than 0.88; see --hero-scale in hero.css.
const REST_SCALE = 0.8;
// How much the near plane grows as it passes the camera.
const NEAR_SCALE = 0.25;
// The topmost opaque row of the plane's alpha, as a fraction of the image's
// own height (measured off both crops; scripts-local/verify-near-rock.mjs
// re-measures it every run). Everything above it is transparent sky, so this
// is what decides when the plane has really left the frame.
const ROCK_TOP = 0.335;

// Read once at mount, the way Home.tsx reads its own gate: the decision picks
// which clip is downloaded, and re-picking it mid-session would fetch a second
// several-megabyte file to show the same valley. Reduced motion renders no
// video at all, so the poster is the whole world and no clip is ever
// requested. The portrait clip belongs to the phone breakpoint hero.css lays
// out for, not to a wider mobile heuristic: an iPad or a small tablet window
// keeps the desktop layout, so it keeps the desktop clip and poster too.
function readMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return { scrub: false, phone: false };
  return {
    scrub: !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    phone: window.matchMedia("(max-width: 767px)").matches,
  };
}

export function ScenicHero({
  method = "video",
}: {
  method?: "shader" | "css" | "video";
}) {
  const ref = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const productRef = useRef<HTMLDivElement>(null);
  const clipRef = useRef<HTMLVideoElement>(null);
  const [scenario, setScenario] = useState(0);
  const [motion] = useState(readMotion);

  // ---- the world clip ----------------------------------------------------
  // Scroll writes a target; the rAF loop below is the only thing that ever
  // writes currentTime. See the contract at the top of this file.
  useEffect(() => {
    const section = ref.current;
    const video = clipRef.current;
    // The stage is read for its height alone: it is the length of the pin, and
    // the map above is written against the pin's share of the track.
    const stage = stageRef.current;
    if (!motion.scrub || !section || !video || !stage) return;

    const source = motion.phone ? CLIP.srcMobile : CLIP.src;
    // 20 ms of clip on a phone, 8 ms on a desktop. A phone decoder cannot
    // service a seek every frame, and 20 ms is under a frame of footage.
    const deadband = motion.phone ? 0.02 : 0.008;

    let disposed = false;
    let objectUrl = "";
    let loading = false;
    let ready = false; // duration is known and seeks resolve
    let painted = false; // a real frame has reached the screen
    let primed = false;
    let priming = false;
    let current = 0;
    let target = 0;
    let frame = 0;
    let stuckAt = 0;
    let live = false;
    let top = 0;
    let height = 0;
    let stageHeight = 1;
    // A visitor who lands away from the hero (a /#plans or /#live-demo link)
    // must not pay for the clip. The Shell jumps to the fragment on a timer
    // after mount, so for those first frames the page is still parked on the
    // hero and the warm window below would say yes and fetch. Hold the first
    // fetch until the landing has resolved: the jump itself fires a scroll,
    // and a fragment that matches nothing fires none, so the backstop below
    // releases the hold and a reader genuinely parked on the hero still gets
    // their clip.
    let landing =
      typeof location !== "undefined" && /^#(?!home$).+/.test(location.hash);
    // The clip is 2.4 MB and the poster is this page's LCP. The hero starts
    // the document, so the warm window below is already true at scroll 0 and
    // the fetch used to begin on mount, where 2.4 MB of video competed with
    // the poster, the two fonts, the stylesheet and the app bundle for the
    // first seconds of the connection. It now waits for the window's own load
    // event, or for the reader's first scroll, whichever comes first: the
    // page finishes arriving before the clip starts, and a reader who moves
    // early is not made to wait for a load event they never see.
    //
    // Measured against the served dist: the clip's request used to go out in
    // the same burst as the poster, the two fonts, the stylesheet and the five
    // JS chunks, a few milliseconds into the page. It now starts after
    // loadEventEnd, by which time every one of those has finished (one
    // recorded run: poster done at 25 ms, loadEventEnd 45 ms, clip requested
    // at 101 ms). The total bytes of a first load are unchanged; the order
    // they arrive in is not, and that is the part a visitor on a real
    // connection feels.
    let opened =
      typeof document !== "undefined" && document.readyState === "complete";

    const measure = () => {
      const rect = section.getBoundingClientRect();
      top = rect.top + window.scrollY;
      height = rect.height;
      // Not a constant, and not the same number the stylesheet asks for: the
      // consent banner shortens the stage while it is open.
      stageHeight = stage.offsetHeight;
    };

    // Frame one when the section's top reaches the viewport bottom, the last
    // frame when its bottom leaves the viewport top. Both ends are clamped to
    // scroll that actually exists: this section starts the document, so it has
    // no entry slide and must still open on frame one.
    const progress = () => {
      const view = window.innerHeight;
      const last = Math.max(document.documentElement.scrollHeight - view, 1);
      const start = top - Math.min(view, top);
      const end = Math.min(top + height, last);
      const span = Math.max(end - start, 1);
      return clipMap(
        clamp((window.scrollY - start) / span),
        // Where in that life the stage stops being pinned.
        (top + height - stageHeight - start) / span,
      );
    };
    const within = (before: number, after: number) => {
      const view = window.innerHeight;
      const y = window.scrollY;
      return y > top - view * before && y < top + height + view * after;
    };

    // Nothing reveals the clip on a timer. Every path below waits on evidence
    // that a frame exists, and a decoder that never produces one leaves the
    // poster up: the poster IS frame one of this clip, so a stage that never
    // swaps shows a still valley rather than a black rectangle.
    const reveal = () => {
      if (painted || disposed) return;
      painted = true;
      video.classList.add("is-painted");
    };
    // A 'seeked' event says the decoder accepted the seek; readyState says it
    // has the frame; one rAF later that frame has been composited.
    const onSeeked = () => {
      if (video.readyState < 2) return;
      requestAnimationFrame(reveal);
    };
    const watchFirstFrame = () => {
      const rvfc = (
        video as HTMLVideoElement & {
          requestVideoFrameCallback?: (cb: () => void) => number;
        }
      ).requestVideoFrameCallback;
      if (typeof rvfc === "function") rvfc.call(video, reveal);
      else video.addEventListener("seeked", onSeeked);
    };

    // iOS will not paint a muted video that has never been handed a user
    // gesture, so a clip can be loaded, seeked, and still show nothing. Prime
    // it the moment it has a source; a restricted device (Low Power Mode)
    // rejects that, and the gesture listeners below retry on the next touch.
    // A primed decoder still shows its stale frame until something asks for a
    // different time, and the deadband will not ask if the playhead is already
    // where it should be, so nudge past it and let the loop walk it back.
    const prime = () => {
      if (primed || priming || !video.src || disposed) return;
      priming = true;
      const settle = () => {
        priming = false;
        primed = true;
        try {
          video.pause();
        } catch {
          /* already paused */
        }
        const duration = video.duration || 1;
        try {
          video.currentTime = Math.min(
            clamp(current) * duration + 0.05,
            duration * 0.999,
          );
        } catch {
          /* not seekable yet */
        }
        kick();
      };
      const release = () => {
        priming = false;
      };
      // A play() promise is allowed to stay pending forever, which would jam
      // `priming` and stop any gesture from retrying. Release it on a timer
      // too; a late resolution still lands in settle(), which is idempotent.
      window.setTimeout(() => {
        if (priming) release();
      }, 2000);
      let started: Promise<void> | undefined;
      try {
        started = video.play();
      } catch {
        release();
        return;
      }
      if (started && started.then) started.then(settle, release);
      else settle();
    };
    const onGesture = () => {
      prime();
      if (primed) {
        removeEventListener("touchend", onGesture);
        removeEventListener("pointerdown", onGesture);
      }
    };

    const onMeta = () => {
      ready = true;
      // Force one seek even when the target is already 0: the reveal waits on
      // a painted frame and the loop only seeks when the time must change, so
      // a clip sitting at the top of its track would never seek, never paint,
      // and never replace its poster until the reader scrolled off zero.
      try {
        video.currentTime = Math.max(target * (video.duration || 1), 0.001);
      } catch {
        /* not seekable yet */
      }
      watchFirstFrame();
      prime();
      kick();
    };

    const load = () => {
      if (loading || disposed) return;
      loading = true;
      // Blob, not a plain src: seeking a streamed mp4 needs HTTP range support
      // from whatever is serving it, and a host without it seeks to nothing.
      fetch(source)
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.blob();
        })
        .then((blob) => {
          if (disposed) return;
          // Listeners and preload BEFORE src: assigning src starts the load,
          // so attaching afterwards and reloading aborts the first request.
          video.addEventListener("loadedmetadata", onMeta);
          video.preload = "auto";
          video.muted = true;
          video.playsInline = true;
          objectUrl = URL.createObjectURL(blob);
          video.src = objectUrl;
        })
        .catch(() => {
          loading = false; // the poster stays up and a later scroll retries
        });
    };

    const tick = () => {
      frame = 0;
      if (disposed || !ready || document.hidden) return;
      // Never queue a seek while the decoder is still resolving the last one:
      // a fast flick otherwise piles them up and freezes the clip. But a seek
      // that never resolves would freeze it for the life of the page through
      // this very guard, so one stuck past 700 ms is re-issued.
      if (video.seeking) {
        const now = performance.now();
        if (!stuckAt) stuckAt = now;
        else if (now - stuckAt > 700) {
          stuckAt = now;
          try {
            video.currentTime += 0.001;
          } catch {
            /* not seekable */
          }
        }
        frame = requestAnimationFrame(tick);
        return;
      }
      stuckAt = 0;
      // Off screen: stop touching the decoder. The playhead keeps the target
      // so the first frame after the stage returns is the right one.
      if (!live) {
        current = target;
        return;
      }
      current += (target - current) * LERP;
      if (Math.abs(target - current) < 0.0005) current = target;
      const duration = video.duration || 1;
      // Never ask for the very last instant: a clip seeked to exactly its
      // duration reports the end and can drop back to a black frame.
      const wanted = Math.min(clamp(current), 0.999) * duration;
      if (Math.abs(video.currentTime - wanted) > deadband) {
        try {
          video.currentTime = wanted;
        } catch {
          /* not seekable */
        }
      }
      // Arrived: nothing left to walk toward and no seek in flight.
      if (current !== target || video.seeking) frame = requestAnimationFrame(tick);
    };
    const kick = () => {
      if (!frame && !document.hidden && live) frame = requestAnimationFrame(tick);
    };

    const scroll = () => {
      target = progress();
      section.style.setProperty("--hero-clip-p", target.toFixed(4));
      live = within(1.25, 1.25);
      // Fetch earlier than we drive: a reader who scrolls briskly would
      // otherwise arrive at a stage that is still downloading.
      if (opened && !landing && within(3, 1.5)) load();
      if (!ready) return;
      if (!live) {
        current = target;
        return;
      }
      kick();
    };
    const onScroll = () => {
      landing = false; // the fragment jump has landed, or the reader moved
      opened = true; // a reader who moves early does not wait for `load`
      scroll();
    };
    const remeasure = () => {
      measure();
      scroll();
    };
    // Nothing fires a scroll when the fragment matches no element, so release
    // the hold on a timer too, comfortably past the Shell's own jump.
    const released = landing
      ? window.setTimeout(() => {
          landing = false;
          scroll();
        }, 400)
      : 0;
    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(frame);
        frame = 0;
      } else kick();
    };

    const onWindowLoad = () => {
      opened = true;
      scroll();
    };
    if (!opened) addEventListener("load", onWindowLoad, { once: true });

    const resize = new ResizeObserver(remeasure);
    resize.observe(section);
    addEventListener("scroll", onScroll, { passive: true });
    addEventListener("resize", remeasure);
    addEventListener("touchend", onGesture, { passive: true });
    addEventListener("pointerdown", onGesture, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    remeasure();

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      clearTimeout(released);
      resize.disconnect();
      removeEventListener("load", onWindowLoad);
      removeEventListener("scroll", onScroll);
      removeEventListener("resize", remeasure);
      removeEventListener("touchend", onGesture);
      removeEventListener("pointerdown", onGesture);
      document.removeEventListener("visibilitychange", onVisibility);
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("seeked", onSeeked);
      if (objectUrl) {
        video.removeAttribute("src");
        try {
          video.load();
        } catch {
          /* detached */
        }
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [motion.scrub, motion.phone]);

  const keyboardInput = useRef(false);
  useEffect(() => {
    const element = ref.current;
    const stage = stageRef.current;
    const product = productRef.current;
    if (!element || !stage || !product) return;
    // The display plane (h1) sits behind the card. The subline, buttons and
    // qualifier live inside it, so the plane carries their fade too on the
    // same 0 to 42% range. The world behind both is the scrubbed clip, driven
    // from the same scroll by the effect above on its own loop; this one
    // paints the card, the copy and the near plane in front of them.
    const heading = element.querySelector<HTMLElement>(".ch-hero-heading")!;
    const near = element.querySelector<HTMLElement>(".ch-hero-foreground")!;
    const media = matchMedia("(prefers-reduced-motion: reduce)");
    const native =
      CSS.supports("animation-timeline: view()") &&
      CSS.supports("animation-range: contain 0% contain 100%");
    let frame = 0,
      measureFrame = 0,
      previous = 0,
      current = 0,
      target = 0;
    let geometry = { top: 0, travel: 1, rise: 0, near: 0 };

    const paint = (progress: number) => {
      const enter = smooth(progress / CARD_END);
      const away = sink(progress / NEAR_END);
      const lift = `translate3d(0,${-170 * smooth(progress / HEAD_END)}px,0)`;
      const fade = String(1 - smooth(progress / HEAD_END));
      const gone = progress >= HEAD_END ? "hidden" : "visible";
      product.style.transform = `translate3d(0,${geometry.rise * enter}px,0) scale(${REST_SCALE + (1 - REST_SCALE) * enter})`;
      heading.style.transform = lift;
      heading.style.opacity = fade;
      heading.style.visibility = gone;
      // The near plane rides the same scroll reading in the same paint pass as
      // the card: one rAF, so the ground and the card can never disagree by a
      // frame. It leaves earlier than the card arrives, because the brief asks
      // for the card fully clear of it by hero progress 0.6.
      near.style.transform = `translate3d(0,${geometry.near * away}px,0) scale(${1 + NEAR_SCALE * away})`;
    };
    const tick = (now: number) => {
      frame = 0;
      const dt = previous ? Math.min(50, now - previous) : 16;
      previous = now;
      current += (target - current) * (1 - Math.exp(-dt / 90));
      if (Math.abs(target - current) < 0.0001) current = target;
      paint(current);
      if (current !== target) frame = requestAnimationFrame(tick);
    };
    const scroll = () => {
      if (native || media.matches || document.hidden) return;
      target = clamp((window.scrollY - geometry.top) / geometry.travel);
      if (!frame) {
        previous = 0;
        frame = requestAnimationFrame(tick);
      }
    };
    const measure = () => {
      measureFrame = 0;
      // All layout reads precede writes. Native scrolling needs no per-frame JS work.
      const rect = element.getBoundingClientRect();
      const stageHeight = stage.offsetHeight;
      // The card starts under the copy stack, never on top of it.
      const stackBottom = heading.offsetTop + heading.offsetHeight;
      element.style.setProperty("--hero-stack-bottom", `${stackBottom}px`);
      const productHeight = product.offsetHeight;
      const productTop = product.offsetTop;
      const header =
        document.querySelector(".rb-header")?.getBoundingClientRect().height ??
        68;
      const landing =
        header + Math.max(18, (stageHeight - header - productHeight) / 2);
      // The plane's own box, from layout rather than from a rect, so the
      // transform that is already on it cannot contaminate the reading.
      const planeTop = near.offsetTop;
      const planeHeight = near.offsetHeight;
      geometry = {
        top: scrollY + rect.top,
        travel: Math.max(1, element.offsetHeight - stageHeight),
        rise: landing - productTop,
        // Exactly the travel that carries the plane's topmost ROCK past the
        // bottom of the stage, once the growth to 1.25 has been taken off the
        // top (the plane scales about its own bottom edge, so growing pulls
        // its silhouette up while the travel pushes it down). A flat 0.7 of a
        // stage used to stand in for this, which was 122px short at 1100x700
        // once the plane's box stopped being a share of the viewport: the
        // ridge's peak stayed in frame through the whole exit. 24px of margin
        // so a sub-pixel layout cannot leave a row of rock on screen.
        near: Math.round(
          stageHeight -
            (planeTop + planeHeight) +
            (1 + NEAR_SCALE) * planeHeight * (1 - ROCK_TOP) +
            24,
        ),
      };
      element.style.setProperty("--hero-rise-target", `${geometry.rise}px`);
      element.style.setProperty("--hero-near-target", `${geometry.near}px`);
      element.dataset.motionDriver = native ? "native" : "raf";
      cancelAnimationFrame(frame);
      frame = 0;
      if (media.matches || native) {
        [heading, near, product].forEach((item) => {
          item.style.removeProperty("transform");
          item.style.removeProperty("opacity");
          item.style.removeProperty("visibility");
        });
      } else {
        current = target = clamp((scrollY - geometry.top) / geometry.travel);
        paint(current);
      }
    };
    const scheduleMeasure = () => {
      if (!measureFrame) measureFrame = requestAnimationFrame(measure);
    };
    const resize = new ResizeObserver(scheduleMeasure);
    resize.observe(stage);
    resize.observe(product);
    const key = (event: KeyboardEvent) => {
      if (event.key === "Tab") keyboardInput.current = true;
    };
    const pointer = () => {
      keyboardInput.current = false;
    };
    const visibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(frame);
        frame = 0;
      } else scheduleMeasure();
    };
    if (!native) addEventListener("scroll", scroll, { passive: true });
    addEventListener("resize", scheduleMeasure);
    document.addEventListener("keydown", key);
    document.addEventListener("pointerdown", pointer, { passive: true });
    document.addEventListener("visibilitychange", visibility);
    media.addEventListener("change", scheduleMeasure);
    measure();
    return () => {
      resize.disconnect();
      cancelAnimationFrame(frame);
      cancelAnimationFrame(measureFrame);
      removeEventListener("scroll", scroll);
      removeEventListener("resize", scheduleMeasure);
      document.removeEventListener("keydown", key);
      document.removeEventListener("pointerdown", pointer);
      document.removeEventListener("visibilitychange", visibility);
      media.removeEventListener("change", scheduleMeasure);
    };
  }, []);

  // The rotation is a handoff on a 4.5s cadence, not a cross-fade: the
  // outgoing word leaves over 260ms, the line is empty for 60ms, and the
  // incoming word arrives over 320ms. 640ms end to end; the curves and the
  // delay that separate them are in hero.css. `leaving` is the word on its way
  // out, cleared once the whole handoff has finished (700ms, comfortably past
  // 640) so a resting word cannot animate back down through the frame.
  const [leaving, setLeaving] = useState<number | null>(null);
  useEffect(() => {
    const media = matchMedia("(prefers-reduced-motion: reduce)");
    let clear = 0;
    const timer = setInterval(() => {
      const heading = ref.current?.querySelector(".ch-hero-heading");
      if (
        media.matches ||
        document.hidden ||
        !heading ||
        Number(getComputedStyle(heading).opacity) < 0.5
      )
        return;
      const rect = heading.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= innerHeight) return;
      setScenario((value) => {
        setLeaving(value);
        return (value + 1) % scenarios.length;
      });
      clearTimeout(clear);
      clear = window.setTimeout(() => setLeaving(null), 700);
    }, 4500);
    return () => {
      clearInterval(timer);
      clearTimeout(clear);
    };
  }, []);

  const revealForKeyboard = () => {
    const section = ref.current;
    const stage = stageRef.current;
    const focused = document.activeElement;
    if (
      !keyboardInput.current ||
      !section ||
      !stage ||
      !(focused instanceof HTMLElement) ||
      matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const visible = focused.getBoundingClientRect();
    const rect = section.getBoundingClientRect();
    const travel = section.offsetHeight - stage.offsetHeight;
    const progress = clamp(-rect.top / Math.max(1, travel));
    if (
      progress < CARD_END * 0.92 &&
      (visible.bottom > innerHeight - 24 || visible.top < 80)
    ) {
      window.scrollTo({
        top: scrollY + rect.top + travel * CARD_END * 0.94,
        behavior: "instant",
      });
    }
  };

  return (
    <section ref={ref} className="ch-hero" id="home">
      <div ref={stageRef} className="ch-hero-stage">
        <div className="ch-hero-world" aria-hidden="true">
          {/* The two posters are different crops, not two sizes of one, so the
              choice is art direction and belongs to <picture>. The <img> still
              names the crop this viewport will land on rather than always the
              desktop one: WebKit starts loading the img's own src before the
              <source> beside it has been parsed, so a phone that named the
              1440 frame here downloaded both. */}
          <picture>
            <source
              media="(max-width: 767px)"
              srcSet={CLIP.posterMobile}
              width={406}
              height={720}
            />
            <img
              className="ch-hero-poster"
              src={motion.phone ? CLIP.posterMobile : CLIP.poster}
              width={motion.phone ? 406 : 1440}
              height={motion.phone ? 720 : 810}
              alt=""
            />
          </picture>
          {motion.scrub && (
            // No src and no <source>: the effect above fetches the clip as a
            // Blob and assigns it, so nothing is requested until the hero is
            // near and the markup can never start a range-request load of its
            // own. Muted, and never given an audio track to play.
            <video
              ref={clipRef}
              className="ch-hero-clip"
              muted
              playsInline
              preload="none"
              disablePictureInPicture
              disableRemotePlayback
              tabIndex={-1}
            />
          )}
        </div>
        <div className="ch-hero-heading">
          <h1 aria-label="We pick up when you’re on a job, off for the weekend, out for lunch, on holiday, or asleep at 2 AM.">
            <span className="ch-hero-lead" aria-hidden="true">
              We pick up when you’re
            </span>
            <span className="ch-hero-rotation" aria-hidden="true">
              {scenarios.map((label, index) => (
                <span
                  key={label}
                  className={
                    index === scenario
                      ? "is-active"
                      : index === leaving
                        ? "is-leaving"
                        : undefined
                  }
                >
                  {label}
                </span>
              ))}
            </span>
          </h1>
          <div className="ch-hero-front">
            <p className="ch-hero-description">
              A better day starts at the front desk.
            </p>
            <div className="ch-hero-actions">
              <Link className="rb-button" to="/book">
                Book a demo <ArrowUpRight size={17} />
              </Link>
              <a className="rb-button rb-secondary" href="#live-demo">
                Try the voice agent <AudioLines size={16} />
              </a>
            </div>
          </div>
        </div>
        <div
          ref={productRef}
          className="ch-hero-product"
          onFocusCapture={revealForKeyboard}
        >
          <HeroWorkspace method={method} />
        </div>
        {/* The near plane, in front of the card (z-index 4 against the card's
            2). Two crops, not two sizes of one, so the choice is art direction
            and belongs to <picture>; and as with the poster above, the <img>
            names the crop this viewport will land on, because WebKit starts
            the img's own src before the <source> beside it has been parsed. */}
        <div className="ch-hero-foreground" aria-hidden="true">
          <picture>
            <source
              media="(max-width: 767px)"
              srcSet={NEAR.srcMobile}
              width={NEAR.widthMobile}
              height={NEAR.heightMobile}
            />
            <img
              src={motion.phone ? NEAR.srcMobile : NEAR.src}
              width={motion.phone ? NEAR.widthMobile : NEAR.width}
              height={motion.phone ? NEAR.heightMobile : NEAR.height}
              alt=""
              decoding="async"
            />
          </picture>
        </div>
        <div className="ch-hero-fade" aria-hidden="true" />
      </div>
    </section>
  );
}
